'use client';

import Grid from "@/components/_rebrand/Grid";
import { TextCombination } from "@/components/_rebrand/TextCombination";
import ThemeWrapper from "@/components/_rebrand/ThemeWrapper";
import { slugifyText } from "@/helpers/slugifyText";
import { ThemeNameType } from "@/tailwind-config/theme.constants";
import cn from "classnames";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { IconName } from "../Icon/Icon.registry";
import { scrollToSection, useActiveSection } from "./ScrollingSection.hooks";
import { Section, SectionProps } from "./Section";
import SectionButton from "./SectionButton";
import { UpperLeftGlow } from "./UpperLeftGlow";

export type ScrollingSectionProps = {
  theme?: ThemeNameType;
  title: string;
  description?: string;
  sections: (SectionProps & { iconName: IconName })[];
}

export const ScrollingSection = ({ theme, title, description, sections }: ScrollingSectionProps) => {
  const sectionIds = useMemo(() => sections.map((section) => slugifyText(section.title)), [sections]);
  const activeSectionId = useActiveSection({ sectionIds, rootMargin: `-50px 0px -50% 0px` });
  const activeSectionIndex = activeSectionId ? sectionIds.indexOf(activeSectionId) : 0;

  return (
    <ThemeWrapper theme={theme || 'theme-white'} className="overflow-x-clip">
      <div className="surface-l0">
        <div className="relative">
          <Grid>
            {/* 
                The top and bottom padding for the component is applied to the topmost and 
                bottom-most sticky elements, respectively.
                this makes it easier to align the sticky behavior to the edges of the component.
            */}
            <div
              className={cn(
                "col-span-4 md:col-span-12",
                "pb-3xl pt-margin-section-md",
              )}
            >
              {/* Upper backrgound pattern */}
              <UpperLeftGlow className="absolute top-0 left-[calc(50%-50vw)] pointer-events-none" />
              <TextCombination className="relative z-10 px-xl md:px-0" type="h1p1" headingLevel="h2" align="left" title={title} description={description} />
            </div>
            {/* Table of contents navigation - desktop only */}
            <div
              className={cn(
                "hidden lg:block",
                "col-span-3",
                // top 120px to account for top nav
                "self-start sticky z-10 top-15 pb-margin-section-md")}
            >
              <div className="flex">
                <div className="relative">
                  <motion.div className="w-px h-6 border-active absolute top-0 left-0"
                    initial={{ top: 0 }}
                    animate={{ top: `calc(48px * ${activeSectionIndex})` }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                  />
                  <div className="w-px surface-l1"
                    style={{ height: `calc(48px * ${sections.length})` }}></div>
                </div>
                <div>
                  {sections.map((section, index) => {
                    const id = sectionIds[index];

                    return (
                      <SectionButton
                        key={id}
                        title={section.label}
                        iconName={section.iconName}
                        isActive={activeSectionId === id}
                        onClick={() => scrollToSection(id)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Shadow container for the scrollable section - desktop only */}
            <div className={cn(
              "hidden lg:block sticky shadow-feature-scroll rounded-l-20",
              "w-svw pb-margin-section-md ",
              "md:col-span-9 md:col-start-4 md:row-start-2"
            )} />
            {/* Scrollable feature column, should be placed within the shadow container above */}
            <div className={cn(
              "col-span-4 md:col-span-12 lg:col-span-8 lg:col-start-5 lg:row-start-2",
              "overflow-hidden",
              "relative rounded-12 px-xl lg:px-0 pb-3xl lg:rounded-0",
              /*
               * Using max-lg: prefix, background color needs to be "unset" on larger screens
               * to allow for seamless shadow transition, and tailwind does not provide a "bg-unset" class.
               */
              "max-lg:surface-l0",
            )}>
              {sections.map((section, index) => (
                <Section key={sectionIds[index]} id={sectionIds[index]} {...section} />
              ))}
            </div>
          </Grid>
        </div>
      </div>
    </ThemeWrapper>
  )
}
